import React, { useState } from 'react';
import { useData } from '../context/DataContext';
import { ArrowRight, Target, Users, Search, TrendingUp, Building, Briefcase, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ConsultationPopup from '../components/ConsultationPopup';

const MergersAcquisitionsPage = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const { data, loading } = useData();

  if (loading || !data || !data.mergersAcquisitionsPageData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  const { hero, phases, services } = data.mergersAcquisitionsPageData;

  const servicesWithIcons = services.map(s => ({
    ...s,
    icon: {
      Target,
      Users,
      Search,
      TrendingUp,
      Building,
      Briefcase
    }[s.icon] || Target
  }));

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex flex-col">
      <Header />
      <ConsultationPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
      
      {/* Hero Section */}
      <section className="pt-32 pb-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-slate-950/80 z-10" />
        <div className="absolute inset-0 z-0 bg-cover bg-center" style={{ backgroundImage: `url('${hero.bgImage}')` }} />
        <div className="container mx-auto px-4 relative z-20">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-6 bg-amber-500/10 text-amber-500 border-amber-500/20">{hero.badge}</Badge>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              {hero.title}
            </h1>
            <p className="text-xl text-slate-300 mb-8 leading-relaxed">
              {hero.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white w-full sm:w-auto" onClick={() => setIsPopupOpen(true)}>
                {hero.ctaText}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-slate-900/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Our Approach</h2>
            <p className="text-xl text-slate-400">A structured process to ensure successful transactions</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {phases.map((phase, index) => (
              <Card key={index} className="bg-slate-800/50 border-slate-700 hover:bg-slate-800/70 transition-all relative overflow-hidden group">
                <div className="absolute top-0 right-0 p-6 opacity-10 font-bold text-6xl text-amber-500 group-hover:scale-110 transition-transform">
                  {phase.number}
                </div>
                <CardContent className="p-8 relative z-10">
                  <h3 className="text-2xl font-bold text-amber-500 mb-2">{phase.title}</h3>
                  <h4 className="text-lg font-semibold text-white mb-4">{phase.subtitle}</h4>
                  <p className="text-slate-400 leading-relaxed">{phase.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Our M&A Services</h2>
            <p className="text-xl text-slate-400">Comprehensive advisory solutions for your strategic needs</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {servicesWithIcons.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Card key={index} className="bg-slate-800/50 border-slate-700 hover:bg-slate-800/70 transition-all">
                  <CardHeader>
                    <div className="w-12 h-12 rounded-lg bg-amber-500/10 flex items-center justify-center mb-4">
                      <IconComponent className="h-6 w-6 text-amber-500" />
                    </div>
                    <CardTitle className="text-lg text-white">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-400 text-sm leading-relaxed">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-blue-950/20" />
        <div className="container mx-auto px-4 relative z-10 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Explore Synergies?</h2>
          <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
            Whether you're looking to acquire, merge, or exit, our team provides the strategic guidance and execution expertise you need.
          </p>
          <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => setIsPopupOpen(true)}>
            Schedule a Confidential Discussion
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MergersAcquisitionsPage;
